import {
  Component,
  Input,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, AbstractControl, Validators, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Folder, FolderUsageType, Partner, Template } from 'src/app/core/services/api-clients';
import { Subscription, BehaviorSubject, Observable } from 'rxjs';
import { take, map, distinctUntilChanged } from 'rxjs/operators';
import { TemplateDataStoreFacade } from 'src/app/template/facades/template-data-store.facade';

@Component({
  selector: 'app-editor-template-form',
  styleUrls: ['./editor-template-form.component.scss'],
  templateUrl: './editor-template-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorTemplateFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public parentForm: FormGroup;
  @Input()
  model: Template;
  @Input()
  options = {
    partners: [] as Partner[],
    folders: [] as Folder[],
    readOnly: false,
  };
  @Input()
  public isRequesting: boolean;
  @Output()
  public updateModelWithChanges = new EventEmitter<Partial<Template>>();
  public form: FormGroup;
  private readonly subscription: Subscription = new Subscription();
  private readonly onChanges = new BehaviorSubject<SimpleChanges>(null);
  private readonly partnerIDSubject = new BehaviorSubject<string>(null);
  public readonly partnerID$ = this.partnerIDSubject.asObservable();

  public readonly folders$ = this.partnerID$.pipe(
    distinctUntilChanged(),
    map((partnerID) => {
      if (partnerID) {
        return this.options.folders.filter((t) => t.usageType !== FolderUsageType.SpaceProgram && t.partnerID === partnerID);
      }
      return [] as Folder[];
    })
  );

  constructor(private fb: FormBuilder, private dataStoreFacade: TemplateDataStoreFacade) {}

  ngOnInit() {
    this.prepareLookups();
    this.initForm();
    if (this.parentForm) {
      this.parentForm.addControl('template', this.form);
    }
    this.subscription.add(
      this.onChanges.subscribe((changes: SimpleChanges) => {
        if (changes?.model) {
          const model = changes.model.currentValue as Template;
          const values = this.modelToForm(model);
          this.form.get('templateName').updateValueAndValidity({ emitEvent: false });
          this.form.patchValue(values, { emitEvent: false });
        }
      })
    );
    this.subscription.add(
      this.form.valueChanges.pipe().subscribe((value) => {
        const values = this.form.getRawValue();
        const partnerID = this.model.partnerID;
        this.model = this.formToModel(values);
        if (this.model.partnerID !== partnerID) {
          this.form.get('templateName')?.updateValueAndValidity();
          this.form.get('folderID').setValue(null);
        }
        this.updateModelWithChanges.emit(this.model);
        this.partnerIDSubject.next(value.partnerID);
      })
    );
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.onChanges.next(changes);
  }
  prepareLookups() {}
  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
  initForm() {
    this.form = this.fb.group({
      templateName: this.fb.control('', [Validators.required], this.validateNameDuplicateValidator()),
      templateDescr: this.fb.control(''),
      folderID: this.fb.control(null),
      partnerID: this.fb.control(null, Validators.required),
    });
    if (this.options.readOnly) {
      this.form.disable({ onlySelf: true });
    }
  }
  formToModel(values: any): Partial<Template> {
    const model: Partial<Template> = {
      ...this.model,
      templateName: values.templateName,
      templateDescr: values.templateDescr,
      folderID: values.folderID,
      partnerID: values.partnerID,
    };
    return model;
  }
  modelToForm(template: Template) {
    return (
      (template && {
        templateName: template.templateName,
        templateDescr: template.templateDescr,
        folderID: template.folderID,
        partnerID: template.partnerID,
      }) ||
      {}
    );
  }
  validateNameDuplicateValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return this.dataStoreFacade.templates$.pipe(
        map((templates) => {
          const duplicate = !!templates.find(
            (f) => f.templateID !== this.model.templateID && f.partnerID === this.model.partnerID && f.templateName === control.value
          );
          if (duplicate) {
            return {
              duplicate: true,
            };
          }
          return null;
        }),
        take(1)
      );
    };
  }
}
